import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ResponsiveOperationalTable from "../../components/dashboard/ResponsiveOperationalTable";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import CardMembershipRoundedIcon from "@mui/icons-material/CardMembershipRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import PauseCircleRoundedIcon from "@mui/icons-material/PauseCircleRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PlayCircleRoundedIcon from "@mui/icons-material/PlayCircleRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { useParams, useSearchParams } from "react-router-dom";
import { planBillingApi } from "../../api/planBilling";
import MercadoPago3DSChallenge from "../../components/payments/MercadoPago3DSChallenge";
import MercadoPagoCardForm from "../../components/payments/MercadoPagoCardForm";
import { useProjects } from "../../context/ProjectsContext";
import { useProjectSettingsDirty } from "../../components/dashboard/ProjectSettingsUnsavedContext";
import { getProjectBillingAccess } from "../../utils/projectBillingAccess";
import {
  billingPeriodOptions,
  quoteMonthlySelection,
  quotePeriodSelection,
} from "../../utils/billingPricing";

/**
 * WEBPROFIT PLAN UI HOTFIX · 2026-09-21
 * Scope: presentation only. API contracts, checkout payloads and billing state remain unchanged.
 * Stability rule: one visible "amount to pay" must come from the same quote used by checkout.
 * TEST STATUS: PASS · TypeScript JSX parse/transpile + UI invariant checks. Full CRA build pending in host repo.
 */
const BRAND = {
  orange: "#F35A3A",
  orangeDark: "#D94B2D",
  navy: "#19242F",
  dark: "var(--wp-panel-text-primary, #19242F)",
  muted: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.64)",
  subtle: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.045)",
  border: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
};

const SERVICE_PRESENTATION = {
  landing: {
    icon: LanguageRoundedIcon,
    eyebrow: "Presencia online",
    benefit: "Mostrá tu negocio, recibí consultas y mantené una web profesional siempre visible.",
  },
  products: {
    icon: Inventory2RoundedIcon,
    eyebrow: "Catálogo",
    benefit: "Publicá productos o servicios con imágenes, precios, categorías y datos adicionales.",
  },
  orders: {
    icon: ShoppingCartRoundedIcon,
    eyebrow: "Ventas",
    benefit: "Recibí pedidos y seguí cada operación desde un flujo simple para tu equipo.",
  },
  payments: {
    icon: PaymentsRoundedIcon,
    eyebrow: "Cobros online",
    benefit: "Aceptá pagos con el Mercado Pago propio de tu comercio, sin mezclar cuentas.",
  },
  crm: {
    icon: PeopleAltRoundedIcon,
    eyebrow: "Relaciones",
    benefit: "Centralizá clientes, contactos e historial para que la relación comercial no dependa de la memoria.",
  },
  memberships: {
    icon: CardMembershipRoundedIcon,
    eyebrow: "Ingresos recurrentes",
    benefit: "Gestioná membresías, vigencias y renovaciones sobre el mismo CRM.",
  },
  system: {
    icon: BusinessCenterRoundedIcon,
    eyebrow: "Gestión",
    benefit: "Ordená movimientos, tareas y procesos internos desde un solo lugar.",
  },
  stats: {
    icon: AnalyticsRoundedIcon,
    eyebrow: "Indicadores",
    benefit: "Entendé la actividad del negocio con métricas fáciles de leer y usar.",
  },
  webpush: {
    icon: NotificationsActiveRoundedIcon,
    eyebrow: "Notificaciones",
    benefit: "Recibí alertas y enviá novedades a las personas suscriptas a tu web.",
  },
  assist: {
    icon: AutoAwesomeRoundedIcon,
    eyebrow: "Asistencia inteligente",
    benefit: "Base para cargar y operar WebProfit con lenguaje natural, voz y automatizaciones confirmables.",
  },
};

function money(value, currency = "ARS") {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    maximumFractionDigits: currency === "ARS" ? 0 : 2,
  }).format(Number(value || 0));
}

function dateLabel(value) {
  if (!value) return "A confirmar";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "A confirmar";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium" }).format(date);
}

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function isValidEmail(value) {
  const email = normalizeEmail(value);
  return email.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function statusLabel(status) {
  const labels = {
    trial: "Prueba gratis activa",
    pending: "Falta terminar el pago",
    active: "Activo",
    past_due: "Pago pendiente",
    paused: "Pausado",
    cancelled: "Cancelado",
    exempt: "Bonificado",
    authorized: "Autorizado",
    manual_pending: "Pendiente",
    pending_payment: "Pago pendiente",
    approved: "Pagado",
    expired: "Vencido",
    rejected: "Rechazado",
  };
  return labels[status] || status || "Sin activar";
}

function statusColor(status) {
  if (["active", "authorized", "approved", "exempt", "trial"].includes(status)) return "success";
  if (["pending", "pending_payment", "manual_pending", "past_due"].includes(status)) return "warning";
  if (["paused", "cancelled", "expired", "rejected"].includes(status)) return "error";
  return "default";
}

function uniqueCodes(values = []) {
  return [...new Set((Array.isArray(values) ? values : []).map(String).filter(Boolean))].sort();
}

function sameCodes(left = [], right = []) {
  const a = uniqueCodes(left);
  const b = uniqueCodes(right);
  return a.length === b.length && a.every((code, index) => code === b[index]);
}

function splitFixedAmount(total, codes = []) {
  const orderedCodes = uniqueCodes(codes);
  if (!orderedCodes.length) return {};
  const totalCents = Math.max(0, Math.round(Number(total || 0) * 100));
  const baseCents = Math.floor(totalCents / orderedCodes.length);
  let remainder = totalCents - (baseCents * orderedCodes.length);
  return orderedCodes.reduce((acc, code) => {
    const cents = baseCents + (remainder > 0 ? 1 : 0);
    if (remainder > 0) remainder -= 1;
    acc[code] = cents / 100;
    return acc;
  }, {});
}

function expandSelection(codes, services) {
  const byCode = new Map((services || []).map((service) => [service.code, service]));
  const selected = new Set();

  const add = (code, trail = []) => {
    if (!code || selected.has(code) || trail.includes(code)) return;
    const service = byCode.get(code);
    if (!service) return;
    for (const dependency of service.requiredServiceCodes || []) {
      add(dependency, [...trail, code]);
    }
    selected.add(code);
  };

  (codes || []).forEach((code) => add(code));
  return (services || [])
    .filter((service) => selected.has(service.code))
    .map((service) => service.code);
}

function removeServiceAndDependants(code, selectedCodes, services) {
  const selected = new Set(selectedCodes || []);
  selected.delete(code);

  let changed = true;
  while (changed) {
    changed = false;
    for (const service of services || []) {
      if (!selected.has(service.code)) continue;
      const missingDependency = (service.requiredServiceCodes || []).some(
        (dependency) => !selected.has(dependency),
      );
      if (missingDependency) {
        selected.delete(service.code);
        changed = true;
      }
    }
  }

  return (services || [])
    .filter((service) => selected.has(service.code))
    .map((service) => service.code);
}

function subscriptionServicesLabel(subscription) {
  const snapshotNames = (subscription?.serviceSnapshot || [])
    .map((service) => service?.name)
    .filter(Boolean);
  if (snapshotNames.length) return snapshotNames.join(" + ");
  const codes = subscription?.requestedServiceCodes || [];
  return codes.length ? codes.join(" + ") : subscription?.requestedPlanCode || "—";
}

function CompactPlanOverview({
  amount,
  currency,
  status,
  statusColorName,
  amountLabel,
  deadlineLabel,
  deadlineValue,
  paymentLabel,
  selectedServices,
  ownerManaged,
  paymentNeedsAttention,
  canManage,
  canCheckout,
  showCheckout = true,
  checkoutLabel,
  onCheckout,
  onEdit,
  storedAmountDiffers,
  storedAmount,
  storedCurrency,
  noticeText,
  noticeSeverity = "info",
}) {
  const visibleServices = selectedServices.slice(0, 4);
  const hiddenCount = Math.max(0, selectedServices.length - visibleServices.length);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3.25,
        border: `1px solid ${BRAND.border}`,
        bgcolor: "var(--wp-panel-surface, #fff)",
        boxShadow: "0 10px 28px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.07)",
        overflow: "hidden",
      }}
    >
      {/*
       * ANTI-SCROLL CONTRACT · Overview must stay compact and action-oriented.
       * TEST STATUS: PASS static JSX parse. Runtime payment flow unchanged.
       */}
      <Box sx={{ px: { xs: 1.45, sm: 1.8 }, py: { xs: 1.35, sm: 1.55 } }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
          spacing={{ xs: 1.25, md: 2 }}
          sx={{ minWidth: 0 }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.8} flexWrap="wrap" useFlexGap>
              <Typography variant="overline" sx={{ color: BRAND.muted, fontWeight: 950, letterSpacing: ".11em", lineHeight: 1.2 }}>
                PLAN Y FACTURACIÓN
              </Typography>
              <Chip size="small" label={status} color={statusColorName} sx={{ height: 25, fontWeight: 900 }} />
            </Stack>

            <Stack direction="row" alignItems="baseline" spacing={0.8} sx={{ mt: 0.55, minWidth: 0 }}>
              <Typography sx={{ color: BRAND.dark, fontSize: { xs: "1.7rem", sm: "2rem" }, lineHeight: 1, fontWeight: 950, letterSpacing: "-.04em" }}>
                {money(amount, currency)}
              </Typography>
              <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 850 }}>
                {amountLabel}
              </Typography>
            </Stack>

            {storedAmountDiffers ? (
              <Typography variant="caption" sx={{ display: "block", mt: 0.45, color: BRAND.muted }}>
                Precio almacenado anterior: {money(storedAmount, storedCurrency || currency)}. El importe efectivo es el total visible.
              </Typography>
            ) : null}
          </Box>

          <Stack
            direction={{ xs: "row", sm: "row" }}
            spacing={0.8}
            sx={{
              width: { xs: "100%", md: "auto" },
              flexShrink: 0,
              "& .MuiButton-root": { minHeight: 40, borderRadius: 2.2, textTransform: "none", fontWeight: 950 },
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              disabled={!canManage || paymentNeedsAttention || ownerManaged}
              onClick={onEdit}
              sx={{ borderColor: BRAND.border, color: BRAND.dark, minWidth: { md: 126 } }}
            >
              {ownerManaged ? "Plan fijado" : "Editar módulos"}
            </Button>
            {showCheckout ? (
              <Button
                fullWidth
                variant="contained"
                disabled={!canCheckout}
                onClick={onCheckout}
                startIcon={<CreditCardRoundedIcon />}
                sx={{ bgcolor: BRAND.orange, minWidth: { md: 148 }, "&:hover": { bgcolor: BRAND.orangeDark } }}
              >
                {checkoutLabel || (paymentNeedsAttention ? "Regularizar pago" : "Ir al pago")}
              </Button>
            ) : null}
          </Stack>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          spacing={0.65}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1.15, minWidth: 0 }}
        >
          <Chip
            size="small"
            icon={<WorkspacePremiumRoundedIcon />}
            label={`${selectedServices.length} ${selectedServices.length === 1 ? "módulo" : "módulos"}`}
            sx={{ height: 26, fontWeight: 900, bgcolor: BRAND.subtle }}
          />
          {visibleServices.map((service) => (
            <Chip key={service.code} size="small" label={service.name} sx={{ height: 26, maxWidth: 170, fontWeight: 800, bgcolor: BRAND.subtle }} />
          ))}
          {hiddenCount ? <Chip size="small" label={`+${hiddenCount}`} sx={{ height: 26, fontWeight: 900 }} /> : null}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 800 }}>
            {deadlineLabel}: <Box component="span" sx={{ color: BRAND.dark, fontWeight: 950 }}>{deadlineValue}</Box>
          </Typography>
          <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 800 }}>
            · {paymentLabel}
          </Typography>
        </Stack>
      </Box>

      {noticeText ? (
        <Alert
          severity={noticeSeverity}
          icon={false}
          sx={{
            py: 0.45,
            px: { xs: 1.45, sm: 1.8 },
            borderRadius: 0,
            borderTop: `1px solid ${BRAND.border}`,
            "& .MuiAlert-message": { py: 0.25, fontSize: ".82rem", fontWeight: 800 },
          }}
        >
          {noticeText}
        </Alert>
      ) : null}
    </Paper>
  );
}

function ServiceTile({
  service,
  selected,
  activeNow,
  selectable,
  presentation,
  requiredNames,
  requiredBy,
  disabled,
  paymentNeedsAttention,
  ownerLocked = false,
  onToggle,
}) {
  const ServiceIcon = presentation.icon || WorkspacePremiumRoundedIcon;
  const [expanded, setExpanded] = React.useState(false);
  const features = (service.features || []).filter(Boolean);
  const lockedByDependency = selected && requiredBy.length > 0;
  const controlDisabled = disabled || !selectable || lockedByDependency;
  const visibleStatus = ownerLocked && selected
    ? "Propietario"
    : !selectable
      ? "No disponible"
      : lockedByDependency
        ? "Dependencia"
        : activeNow && !selected
          ? "Se quitará"
          : null;
  const stateDescription = ownerLocked && selected
    ? "Módulo activado por el propietario desde Owner. No puede modificarse desde este panel."
    : !selectable
      ? "No disponible para nuevas altas."
      : lockedByDependency
        ? `Seleccionado como dependencia de ${requiredBy.join(", ")}. No se puede desactivar mientras esas capacidades sigan seleccionadas.`
        : activeNow && !selected
          ? "No seleccionado. Se quitará cuando se apliquen los cambios."
          : selected
            ? activeNow
              ? "Seleccionado. Ya forma parte del plan actual."
              : "Seleccionado. Se agregará cuando se apliquen los cambios."
            : "No seleccionado.";
  const globalDisabledDescription = controlDisabled && !lockedByDependency && selectable
    ? ownerLocked
      ? " Módulos activados por el propietario: la selección está bloqueada."
      : paymentNeedsAttention
        ? " La edición está bloqueada hasta regularizar el pago."
        : " La selección no se puede modificar ahora."
    : "";

  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,
        overflow: "hidden",
        borderRadius: 2.75,
        border: selected ? `2px solid ${BRAND.orange}` : `1px solid ${BRAND.border}`,
        bgcolor: selected ? "rgba(243,90,58,.035)" : "var(--wp-panel-surface, #fff)",
        boxShadow: selected ? "0 7px 20px rgba(243,90,58,.10)" : "0 4px 14px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.045)",
        opacity: !selectable && !activeNow ? 0.68 : 1,
        transition: "border-color .16s ease, background-color .16s ease, box-shadow .16s ease",
      }}
    >
      <Box sx={{ position: "relative", minWidth: 0 }}>
        <ButtonBase
          role="checkbox"
          aria-checked={selected}
          aria-disabled={controlDisabled}
          aria-label={`${service.name}. ${ownerLocked ? "Incluido en el precio acordado" : `${money(service.monthlyPrice, service.currency)} por mes`}. ${stateDescription}${globalDisabledDescription}`}
          disableRipple={controlDisabled}
          onClick={() => {
            if (controlDisabled) return;
            onToggle(!selected);
          }}
          sx={{
            width: "100%",
            minHeight: { xs: 108, md: 82 },
            p: { xs: 1.15, md: 1 },
            pr: 4.8,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "space-between",
            gap: { xs: 0.8, md: 0.55 },
            textAlign: "left",
            borderRadius: 2.5,
            cursor: controlDisabled ? "default" : "pointer",
            "&.Mui-focusVisible": {
              outline: `3px solid rgba(243,90,58,.38)`,
              outlineOffset: -3,
            },
            "&:hover": controlDisabled
              ? undefined
              : { bgcolor: selected ? "rgba(243,90,58,.055)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.025)" },
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.75} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: selected ? "rgba(243,90,58,.12)" : BRAND.subtle,
                color: selected ? BRAND.orange : BRAND.dark,
                flexShrink: 0,
              }}
            >
              <ServiceIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box
              aria-hidden="true"
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                border: selected ? "none" : `2px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.24)`,
                bgcolor: selected ? (lockedByDependency ? "rgba(7,90,121,.12)" : "rgba(243,90,58,.12)") : "transparent",
                color: lockedByDependency ? "#075A79" : BRAND.orange,
                flexShrink: 0,
              }}
            >
              {selected ? (lockedByDependency ? <LockRoundedIcon sx={{ fontSize: 15 }} /> : <CheckCircleRoundedIcon sx={{ fontSize: 18 }} />) : null}
            </Box>
          </Stack>

          <Typography
            sx={{
              color: BRAND.dark,
              fontWeight: 950,
              fontSize: { xs: ".91rem", sm: ".96rem" },
              lineHeight: 1.18,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              overflowWrap: "anywhere",
              minHeight: "2.25em",
            }}
          >
            {service.name}
          </Typography>

          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: BRAND.dark, fontWeight: 950, lineHeight: 1.05, fontSize: { xs: ".96rem", sm: "1rem" } }}>
              {ownerLocked ? "Incluido" : money(service.monthlyPrice, service.currency)}
            </Typography>
            <Stack direction="row" spacing={0.55} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.35, minHeight: 22 }}>
              <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 750 }}>
                {ownerLocked ? "en el precio acordado" : "/mes"}
              </Typography>
              {visibleStatus ? (
                <Chip
                  size="small"
                  label={visibleStatus}
                  icon={lockedByDependency ? <LockRoundedIcon /> : undefined}
                  sx={{
                    height: 21,
                    maxWidth: "100%",
                    fontSize: 9.5,
                    fontWeight: 950,
                    bgcolor: !selectable
                      ? "rgba(211,47,47,.08)"
                      : lockedByDependency
                        ? "rgba(2,136,209,.10)"
                        : "rgba(211,47,47,.08)",
                    color: !selectable || activeNow && !selected ? "#B3261E" : "#075A79",
                    "& .MuiChip-icon": { fontSize: 13, color: "inherit" },
                    "& .MuiChip-label": { px: 0.65, overflow: "hidden", textOverflow: "ellipsis" },
                  }}
                />
              ) : null}
            </Stack>
          </Box>
        </ButtonBase>

        <IconButton
          size="small"
          aria-label={`${expanded ? "Ocultar" : "Ver"} detalles de ${service.name}`}
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          sx={{
            position: "absolute",
            right: 5,
            bottom: 5,
            width: 40,
            height: 40,
            color: BRAND.muted,
            bgcolor: "var(--wp-panel-surface, #fff)",
            "&:hover": { bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.06)", color: BRAND.dark },
            "&.Mui-focusVisible": { outline: `2px solid ${BRAND.orange}`, outlineOffset: 1 },
          }}
        >
          {expanded ? <ExpandLessRoundedIcon fontSize="small" /> : <InfoOutlinedIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 1.2, pb: 1.2 }}>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.4 }}>
            {presentation.benefit || service.description || "Herramientas principales de esta capacidad."}
          </Typography>
          {service.customPriceApplied ? (
            <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: BRAND.orangeDark, fontWeight: 900 }}>
              Este proyecto tiene un precio especial para esta capacidad.
            </Typography>
          ) : null}
          {requiredNames.length ? (
            <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: BRAND.muted, fontWeight: 800 }}>
              Al elegirlo también se selecciona: {requiredNames.join(", ")}.
            </Typography>
          ) : null}
          {lockedByDependency ? (
            <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "#075A79", fontWeight: 850 }}>
              Dependencia de: {requiredBy.join(", ")}.
            </Typography>
          ) : null}
          <Box sx={{ mt: 0.9, display: "grid", gap: 0.55 }}>
            {(features.length ? features : ["Herramientas principales del módulo"]).map((feature) => (
              <Stack key={feature} direction="row" spacing={0.65} alignItems="flex-start" sx={{ minWidth: 0 }}>
                <CheckCircleRoundedIcon sx={{ mt: 0.1, fontSize: 15, color: "success.main", flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: BRAND.dark, lineHeight: 1.35 }}>
                  {feature}
                </Typography>
              </Stack>
            ))}
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}

function BillingPaymentChoice({
  paymentMode,
  amount,
  currency,
  publicKey,
  embeddedCardConfigured,
  payerEmail,
  onPayerEmailChange,
  payerEmailRequired,
  fallbackDisabled,
  fallbackLabel,
  onFallback,
  working,
  cardDisabled,
  onCardSubmit,
  cardError,
  threeDSChallenge,
  onThreeDSComplete,
  recurringActive,
}) {
  // STEP-GATED PAYMENT METHOD · Do not mount the card form until the user explicitly chooses it.
  // TEST STATUS: PASS static state-transition check; provider requests remain untouched.
  const [channel, setChannel] = React.useState(null);

  React.useEffect(() => {
    setChannel(null);
  }, [paymentMode]);

  React.useEffect(() => {
    if (!embeddedCardConfigured && channel === "card") {
      setChannel(null);
    }
  }, [channel, embeddedCardConfigured]);

  return (
    <Box>
      <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 950 }}>
        DÓNDE QUERÉS PAGAR
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={channel}
        onChange={(_event, value) => value && setChannel(value)}
        sx={{
          mt: 1,
          "& .MuiToggleButton-root": {
            minWidth: 0,
            py: 1,
            px: 1,
            gap: 0.65,
            textTransform: "none",
            fontSize: ".82rem",
            fontWeight: 900,
          },
        }}
      >
        <ToggleButton value="card" disabled={!embeddedCardConfigured}>
          <CreditCardRoundedIcon sx={{ fontSize: 18 }} />
          Tarjeta acá
        </ToggleButton>
        <ToggleButton value="mercadopago">
          <AccountBalanceWalletRoundedIcon sx={{ fontSize: 18 }} />
          Mercado Pago
        </ToggleButton>
      </ToggleButtonGroup>

      {!channel ? (
        <Typography variant="body2" sx={{ mt: 1.15, color: BRAND.muted, lineHeight: 1.45 }}>
          Elegí un medio de pago. Los datos de tarjeta aparecen recién en el siguiente paso.
        </Typography>
      ) : null}

      <Collapse in={channel === "card"} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: 2.5,
            border: `1px solid ${BRAND.border}`,
            bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.018)",
          }}
        >
          {!embeddedCardConfigured ? (
            <Alert severity="warning">
              Falta configurar la Public Key de Mercado Pago para mostrar la tarjeta dentro del panel.
            </Alert>
          ) : threeDSChallenge ? (
            <MercadoPago3DSChallenge
              externalResourceUrl={threeDSChallenge.externalResourceUrl}
              creq={threeDSChallenge.creq}
              onComplete={onThreeDSComplete}
            />
          ) : (
            <MercadoPagoCardForm
              key={`${paymentMode}-${amount}-${publicKey}`}
              publicKey={publicKey}
              amount={amount}
              currency={currency}
              buyer={{ email: payerEmail }}
              processing={working}
              disabled={cardDisabled}
              compact
              hideHeader
              submitLabel={
                paymentMode === "recurring"
                  ? recurringActive
                    ? "Actualizar esta tarjeta"
                    : "Suscribirme con esta tarjeta"
                  : `Pagar ${money(amount, currency)}`
              }
              onSubmit={onCardSubmit}
              onError={() => {}}
            />
          )}
          {cardError ? (
            <Alert severity="error" sx={{ mt: 1.25 }}>
              {cardError}
            </Alert>
          ) : null}
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1.1, color: BRAND.muted, lineHeight: 1.45 }}
          >
            {paymentMode === "recurring"
              ? "La tarjeta queda asociada al débito mensual. El correo del formulario puede ser distinto al de WebProfit."
              : "Es un único cobro por el período. WebProfit no guarda los datos de la tarjeta."}
          </Typography>
        </Box>
      </Collapse>

      <Collapse in={channel === "mercadopago"} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mt: 1.5,
            p: 1.6,
            borderRadius: 2.5,
            border: `1px solid ${BRAND.border}`,
            bgcolor: BRAND.subtle,
          }}
        >
          {paymentMode === "recurring" ? (
            <TextField
              fullWidth
              size="small"
              type="email"
              label="Correo de la cuenta de Mercado Pago"
              value={payerEmail}
              onChange={(event) => onPayerEmailChange(event.target.value)}
              error={payerEmailRequired && payerEmail.length > 0 && !isValidEmail(payerEmail)}
              helperText={
                payerEmailRequired && !payerEmail
                  ? "Ingresá el correo que autorizará el débito."
                  : "Puede ser distinto al correo de WebProfit."
              }
            />
          ) : null}
          <Typography
            variant="body2"
            sx={{ mt: paymentMode === "recurring" ? 1.25 : 0, color: BRAND.muted, lineHeight: 1.5 }}
          >
            {paymentMode === "recurring"
              ? "Te llevamos a Mercado Pago para autorizar la suscripción con tu cuenta o una tarjeta disponible."
              : "Te llevamos a Mercado Pago para pagar con saldo, tarjeta u otro medio habilitado."}
          </Typography>
          <Button
            fullWidth
            disabled={fallbackDisabled}
            onClick={onFallback}
            variant="contained"
            startIcon={
              working ? (
                <WebProfitSpinner size={18} color="inherit" />
              ) : (
                <AccountBalanceWalletRoundedIcon />
              )
            }
            sx={{
              mt: 1.5,
              minHeight: 46,
              bgcolor: "#009EE3",
              borderRadius: 2.25,
              textTransform: "none",
              fontWeight: 950,
              "&:hover": { bgcolor: "#008CC9" },
            }}
          >
            {fallbackLabel}
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
}

function CheckoutSummary({
  selectedServices,
  selectedAmount,
  monthlyPricing,
  periodPricing,
  periodMonths,
  periodOptions,
  onPeriodMonthsChange,
  currency,
  billing,
  billingAccess,
  paymentNeedsAttention,
  paymentMode,
  onPaymentModeChange,
  payerEmail,
  onPayerEmailChange,
  payerEmailRequired,
  recurringLocked,
  oneTimeLocked,
  selectionChanged,
  actionLabel,
  primaryDisabled,
  primaryAction,
  working,
  mercadoPagoPublicKey,
  embeddedCardConfigured,
  cardDisabled,
  onCardSubmit,
  cardError,
  threeDSChallenge,
  onThreeDSComplete,
  recurringActive,
  onClose,
}) {
  const paymentSuspended = billingAccess?.reason === "billing_suspended";
  const [flowStep, setFlowStep] = React.useState(1);
  const displayAmount = paymentMode === "one_time"
    ? Number(periodPricing?.total || selectedAmount || 0)
    : Number(selectedAmount || 0);
  const selectedPeriodLabel = periodMonths === 1 ? "1 mes" : `${periodMonths} meses`;
  const storedMonthlyAmount = Number(billing?.monthlyAmount || 0);
  const showPreviousPrice = Boolean(
    paymentMode === "recurring"
      && storedMonthlyAmount > 0
      && Math.abs(storedMonthlyAmount - displayAmount) >= 0.01,
  );
  const modeLocked = (paymentMode === "one_time" && oneTimeLocked)
    || (paymentMode === "recurring" && recurringLocked);
  const emailMissing = payerEmailRequired && !isValidEmail(payerEmail);
  const canContinue = selectedServices.length > 0 && !modeLocked && !emailMissing;
  const visibleServices = selectedServices.slice(0, 5);
  const hiddenServiceCount = Math.max(0, selectedServices.length - visibleServices.length);

  React.useEffect(() => {
    // Changing payment type invalidates the next visual step, but never changes backend state.
    setFlowStep(1);
  }, [paymentMode]);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3.25,
        border: `1px solid ${BRAND.border}`,
        bgcolor: "var(--wp-panel-surface, #fff)",
        boxShadow: "0 12px 30px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.07)",
        overflow: "hidden",
      }}
    >
      {/*
       * PAYMENT WIZARD · Payment fields are intentionally lazy-mounted.
       * This keeps the billing screen anti-scroll until the customer chooses to pay.
       * TEST STATUS: PASS static JSX parse + step gating invariant.
       */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ px: { xs: 1.5, sm: 1.8 }, py: 1.3, borderBottom: `1px solid ${BRAND.border}` }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 950, letterSpacing: ".08em" }}>
            PAGO · PASO {flowStep} DE 2
          </Typography>
          <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: { xs: "1.15rem", sm: "1.28rem" }, lineHeight: 1.15 }}>
            {flowStep === 1 ? "Confirmá qué vas a pagar" : "Elegí cómo pagarlo"}
          </Typography>
        </Box>
        <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 900, color: BRAND.muted }}>
          Cerrar
        </Button>
      </Stack>

      {flowStep === 1 ? (
        <Box sx={{ p: { xs: 1.5, sm: 1.8 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) minmax(260px,.72fr)" },
              gap: { xs: 1.4, md: 2 },
              alignItems: "start",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="baseline" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Typography sx={{ color: BRAND.dark, fontSize: { xs: "2rem", sm: "2.35rem" }, fontWeight: 950, lineHeight: 1, letterSpacing: "-.045em" }}>
                  {money(displayAmount, currency)}
                </Typography>
                <Typography variant="body2" sx={{ color: BRAND.muted, fontWeight: 850 }}>
                  {paymentMode === "one_time" ? selectedPeriodLabel : "/ mes"}
                </Typography>
                <Chip
                  size="small"
                  label={billingAccess.isGrace
                    ? "Pago pendiente"
                    : paymentSuspended
                      ? "Suspendido"
                      : selectionChanged
                        ? "Cambios sin confirmar"
                        : statusLabel(billing.status)}
                  color={paymentSuspended ? "error" : billingAccess.isGrace || selectionChanged ? "warning" : statusColor(billing.status)}
                  sx={{ height: 25, fontWeight: 900 }}
                />
              </Stack>

              {showPreviousPrice ? (
                <Typography variant="caption" sx={{ display: "block", mt: 0.45, color: BRAND.muted }}>
                  Valor anterior registrado: {money(storedMonthlyAmount, billing?.currency || currency)} / mes.
                </Typography>
              ) : null}

              <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.55} sx={{ mt: 1.15 }}>
                {visibleServices.map((service) => (
                  <Chip key={service.code} size="small" icon={<CheckCircleRoundedIcon />} label={service.name} sx={{ height: 25, fontWeight: 800, bgcolor: BRAND.subtle }} />
                ))}
                {hiddenServiceCount ? <Chip size="small" label={`+${hiddenServiceCount} más`} sx={{ height: 25, fontWeight: 900 }} /> : null}
              </Stack>

              {Number(monthlyPricing?.savings || 0) > 0 ? (
                <Typography variant="caption" sx={{ display: "block", mt: 0.8, color: "success.main", fontWeight: 900 }}>
                  Ahorrás {money(monthlyPricing.savings, currency)} con esta combinación.
                </Typography>
              ) : null}
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ display: "block", color: BRAND.muted, fontWeight: 950 }}>
                MODALIDAD
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={paymentMode}
                onChange={(_event, value) => value && onPaymentModeChange(value)}
                sx={{ mt: 0.65, "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 900, py: 0.8 } }}
              >
                <ToggleButton value="recurring" disabled={recurringLocked}>Automático</ToggleButton>
                <ToggleButton value="one_time" disabled={oneTimeLocked}>Solo este período</ToggleButton>
              </ToggleButtonGroup>

              {paymentMode === "one_time" ? (
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={periodMonths}
                  onChange={(_event, value) => value && onPeriodMonthsChange(value)}
                  sx={{
                    mt: 0.75,
                    display: "grid",
                    gridTemplateColumns: "repeat(4,minmax(0,1fr))",
                    gap: 0.45,
                    "& .MuiToggleButtonGroup-grouped": {
                      m: "0 !important",
                      border: `1px solid ${BRAND.border} !important`,
                      borderRadius: "8px !important",
                      textTransform: "none",
                      fontWeight: 900,
                      minWidth: 0,
                      px: 0.35,
                    },
                  }}
                >
                  {periodOptions.map((option) => (
                    <ToggleButton key={option.months} value={option.months}>
                      {option.months}m{option.discountPercent > 0 ? ` · -${option.discountPercent}%` : ""}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              ) : null}

              {payerEmailRequired && paymentMode === "recurring" ? (
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  label="Correo para Mercado Pago"
                  value={payerEmail}
                  onChange={(event) => onPayerEmailChange(event.target.value)}
                  error={payerEmail.length > 0 && !isValidEmail(payerEmail)}
                  helperText={emailMissing ? "Ingresá un correo válido para continuar." : "Puede ser distinto al correo de WebProfit."}
                  sx={{ mt: 0.85 }}
                />
              ) : null}
            </Box>
          </Box>

          {recurringLocked || oneTimeLocked ? (
            <Alert severity="info" sx={{ mt: 1.2, py: 0 }}>
              {recurringLocked
                ? "El período actual ya está cubierto; evitamos generar un cobro duplicado."
                : "Hay una renovación automática activa; evitamos generar un segundo cobro."}
            </Alert>
          ) : null}

          <Stack direction={{ xs: "column-reverse", sm: "row" }} justifyContent="flex-end" spacing={0.8} sx={{ mt: 1.35 }}>
            <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 900, color: BRAND.muted }}>
              Volver
            </Button>
            <Button
              variant="contained"
              disabled={!canContinue}
              onClick={() => setFlowStep(2)}
              sx={{ bgcolor: BRAND.orange, borderRadius: 2.2, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: BRAND.orangeDark } }}
            >
              Elegir medio de pago
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box sx={{ p: { xs: 1.5, sm: 1.8 } }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={0.8} sx={{ mb: 1.2 }}>
            <Box>
              <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 900 }}>TOTAL CONFIRMADO</Typography>
              <Typography sx={{ color: BRAND.dark, fontSize: "1.45rem", fontWeight: 950, lineHeight: 1.05 }}>
                {money(displayAmount, currency)}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={paymentMode === "one_time" ? `${selectedPeriodLabel} · sin renovación` : "Renovación mensual"}
              sx={{ fontWeight: 900, bgcolor: BRAND.subtle }}
            />
          </Stack>

          <BillingPaymentChoice
            paymentMode={paymentMode}
            amount={displayAmount}
            currency={currency}
            publicKey={mercadoPagoPublicKey}
            embeddedCardConfigured={embeddedCardConfigured}
            payerEmail={payerEmail}
            onPayerEmailChange={onPayerEmailChange}
            payerEmailRequired={payerEmailRequired}
            fallbackDisabled={primaryDisabled}
            fallbackLabel={actionLabel}
            onFallback={primaryAction}
            working={working}
            cardDisabled={cardDisabled}
            onCardSubmit={onCardSubmit}
            cardError={cardError}
            threeDSChallenge={threeDSChallenge}
            onThreeDSComplete={onThreeDSComplete}
            recurringActive={recurringActive}
          />

          <Button onClick={() => setFlowStep(1)} sx={{ mt: 1, textTransform: "none", fontWeight: 900, color: BRAND.muted }}>
            ← Cambiar modalidad
          </Button>
        </Box>
      )}

      <Typography variant="caption" sx={{ display: "block", px: 1.8, py: 0.85, borderTop: `1px solid ${BRAND.border}`, color: BRAND.muted, textAlign: "center" }}>
        Mercado Pago procesa el cobro. Los datos de tarjeta se solicitan sólo cuando elegís ese medio.
      </Typography>
    </Paper>
  );
}

export default function PlanBillingPage() {
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loadProjects, setActiveProject } = useProjects();
  const [data, setData] = React.useState(null);
  const [selectedCodes, setSelectedCodes] = React.useState([]);
  const [paymentMode, setPaymentMode] = React.useState("recurring");
  const [periodMonths, setPeriodMonths] = React.useState(1);
  const [payerEmail, setPayerEmail] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [working, setWorking] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [cardError, setCardError] = React.useState("");
  const [threeDSChallenge, setThreeDSChallenge] = React.useState(null);
  const [workspaceMode, setWorkspaceMode] = React.useState("overview");
  const returnHandled = React.useRef(false);
  const featureWorkspaceOpened = React.useRef(false);
  const paymentModeInitialized = React.useRef(false);
  const periodMonthsInitialized = React.useRef(false);
  const payerEmailInitialized = React.useRef(false);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await planBillingApi.getProject(projectId);
      setData(response);
      if (response.project) setActiveProject(response.project);
    } catch (err) {
      setError(err.message || "No se pudo cargar tu plan");
    } finally {
      setLoading(false);
    }
  }, [projectId, setActiveProject]);

  React.useEffect(() => {
    paymentModeInitialized.current = false;
    periodMonthsInitialized.current = false;
    payerEmailInitialized.current = false;
    returnHandled.current = false;
    featureWorkspaceOpened.current = false;
    setWorkspaceMode("overview");
    load();
  }, [load]);

  React.useEffect(() => {
    const services = data?.services || [];
    const pending = data?.billing?.pendingServiceCodes || [];
    const current = data?.billing?.serviceCodes || [];
    setSelectedCodes(expandSelection(pending.length ? pending : current, services));
    if (!paymentModeInitialized.current && data?.billing) {
      setPaymentMode(
        data.billing.paymentMode === "one_time" ? "one_time" : "recurring",
      );
      paymentModeInitialized.current = true;
    }
    if (!periodMonthsInitialized.current) {
      const pendingCheckout = (data?.checkouts || []).find((checkout) =>
        checkout?.status === "pending_payment" &&
        (!data?.billing?.pendingCheckoutId ||
          String(checkout.id || checkout._id) === String(data.billing.pendingCheckoutId)),
      );
      setPeriodMonths(Number(pendingCheckout?.periodMonths || 1));
      periodMonthsInitialized.current = true;
    }
    if (!payerEmailInitialized.current && data?.billing) {
      const lastSubscriptionEmail = (data.subscriptions || []).find(
        (subscription) => subscription?.payerEmail,
      )?.payerEmail;
      setPayerEmail(
        normalizeEmail(data.billing.payerEmail || lastSubscriptionEmail || ""),
      );
      payerEmailInitialized.current = true;
    }
  }, [data]);

  const billingAreaDirty = React.useMemo(() => {
    if (!data?.billing || loading) return false;
    const services = data.services || [];
    const pending = data.billing.pendingServiceCodes || [];
    const current = data.billing.serviceCodes || [];
    const baselineCodes = expandSelection(pending.length ? pending : current, services).map(String).sort();
    const currentCodes = selectedCodes.map(String).sort();
    const baselineMode = data.billing.paymentMode === "one_time" ? "one_time" : "recurring";
    const lastSubscriptionEmail = (data.subscriptions || []).find((subscription) => subscription?.payerEmail)?.payerEmail;
    const baselineEmail = normalizeEmail(data.billing.payerEmail || lastSubscriptionEmail || "");
    return JSON.stringify(currentCodes) !== JSON.stringify(baselineCodes)
      || paymentMode !== baselineMode
      || (paymentMode === "one_time" && periodMonths !== 1)
      || normalizeEmail(payerEmail) !== baselineEmail;
  }, [data, loading, selectedCodes, paymentMode, periodMonths, payerEmail]);
  useProjectSettingsDirty("billing", billingAreaDirty);

  React.useEffect(() => {
    const paymentId =
      searchParams.get("payment_id") || searchParams.get("collection_id") || "";
    const returned =
      searchParams.get("subscription") ||
      searchParams.get("preapproval_id") ||
      searchParams.get("billing_payment") ||
      searchParams.get("status") ||
      paymentId;
    if (!returned || returnHandled.current) return;
    returnHandled.current = true;

    setWorking(true);
    planBillingApi
      .refresh(projectId, paymentId ? { paymentId } : {})
      .then(async () => {
        setMessage("Listo. Revisamos Mercado Pago y actualizamos tu plan.");
        await Promise.all([load(), loadProjects()]);
        const next = new URLSearchParams(searchParams);
        [
          "subscription",
          "preapproval_id",
          "billing_payment",
          "payment_id",
          "collection_id",
          "collection_status",
          "external_reference",
          "payment_type",
          "merchant_order_id",
          "preference_id",
          "site_id",
          "processing_mode",
          "merchant_account_id",
          "status",
        ].forEach((key) => next.delete(key));
        setSearchParams(next, { replace: true });
      })
      .catch((err) => setError(err.message || "No se pudo verificar el pago"))
      .finally(() => setWorking(false));
  }, [load, loadProjects, projectId, searchParams, setSearchParams]);

  const run = async (operation, successMessage) => {
    try {
      setWorking(true);
      setError("");
      setMessage("");
      const response = await operation();
      setMessage(response?.message || successMessage);
      await Promise.all([load(), loadProjects()]);
      return response;
    } catch (err) {
      setError(err.message || "No se pudo completar la operación");
      return null;
    } finally {
      setWorking(false);
    }
  };

  const billing = data?.billing || {};
  const ownerManagedPricing = billing?.ownerManagedPricing || null;
  const ownerFullSystem = Boolean(
    ownerManagedPricing?.linked
      && ownerManagedPricing?.accessScope === "full_system"
      && ownerManagedPricing?.pricingMode === "fixed_total",
  );
  const billingAccess = getProjectBillingAccess({
    ...(data?.project || {}),
    billing,
  });
  const trialActive = billingAccess.isTrial && !billingAccess.trialExpired;
  const paymentSuspended = billingAccess.reason === "billing_suspended";
  const paymentNeedsAttention = billingAccess.needsPaymentAction;
  const services = data?.services || [];
  const pricingPolicy = data?.pricingPolicy || {};
  const periodOptions = billingPeriodOptions(pricingPolicy);
  const serviceByCode = new Map(services.map((service) => [service.code, service]));
  const selectedServices = services.filter((service) => selectedCodes.includes(service.code));
  const catalogMonthlyPricing = quoteMonthlySelection(selectedServices, pricingPolicy);
  const selectedAmount = ownerFullSystem
    ? Number(ownerManagedPricing?.fixedMonthlyAmount || billing.monthlyAmount || 0)
    : catalogMonthlyPricing.total;
  const ownerAllocationRows = Array.isArray(ownerManagedPricing?.moduleAllocation)
    ? ownerManagedPricing.moduleAllocation
    : [];
  const promotionalAllocation = ownerFullSystem
    ? (ownerAllocationRows.length
        ? ownerAllocationRows.reduce((acc, row) => {
            if (row?.code) acc[String(row.code)] = Number(row.amount || 0);
            return acc;
          }, {})
        : splitFixedAmount(selectedAmount, selectedCodes))
    : {};
  const displayedSelectedServices = ownerFullSystem
    ? selectedServices.map((service) => ({
        ...service,
        monthlyPrice: Number(promotionalAllocation[service.code] || 0),
        customPriceApplied: false,
      }))
    : selectedServices;
  const monthlyPricing = ownerFullSystem
    ? {
        ...catalogMonthlyPricing,
        subtotal: selectedAmount,
        total: selectedAmount,
        savings: 0,
        commercialOverride: { active: true, mode: "fixed_total" },
      }
    : catalogMonthlyPricing;
  const periodPricing = quotePeriodSelection(monthlyPricing, periodMonths, pricingPolicy);
  const checkoutAmount = paymentMode === "one_time" ? periodPricing.total : selectedAmount;
  const currency = selectedServices[0]?.currency || billing.currency || "ARS";
  const serviceOptions = services.map((service) => {
    const selected = selectedCodes.includes(service.code);
    const activeNow = (billing.serviceCodes || []).includes(service.code);
    const selectable = service.active !== false || activeNow;
    const presentation = SERVICE_PRESENTATION[service.code] || {};
    const requiredNames = (service.requiredServiceCodes || [])
      .map((code) => serviceByCode.get(code)?.name)
      .filter(Boolean);
    const requiredBy = services
      .filter(
        (candidate) =>
          selectedCodes.includes(candidate.code) &&
          (candidate.requiredServiceCodes || []).includes(service.code),
      )
      .map((candidate) => candidate.name);

    const displayService = ownerFullSystem && selected
      ? {
          ...service,
          monthlyPrice: Number(promotionalAllocation[service.code] || 0),
          customPriceApplied: false,
        }
      : service;

    return {
      service: displayService,
      selected,
      activeNow,
      selectable,
      presentation,
      requiredNames,
      requiredBy,
      ownerLocked: ownerFullSystem,
    };
  });
  const currentCodes = billing.pendingServiceCodes?.length
    ? billing.pendingServiceCodes
    : billing.serviceCodes || [];
  const selectionChanged = !sameCodes(selectedCodes, currentCodes);
  const recurringPendingUrl =
    billing.pendingSubscriptionUrl ||
    (billing.status === "pending" ? billing.subscriptionUrl : "");
  const periodPendingUrl = billing.pendingCheckoutUrl || "";
  const pendingCheckoutUrl =
    paymentMode === "one_time" ? periodPendingUrl : recurringPendingUrl;
  const pendingRecord = paymentMode === "one_time"
    ? (data?.checkouts || []).find(
        (checkout) =>
          checkout.status === "pending_payment" &&
          (!billing.pendingCheckoutId ||
            String(checkout.id || checkout._id) === String(billing.pendingCheckoutId)),
      )
    : (data?.subscriptions || []).find((subscription) => {
        if (subscription.status !== "pending") return false;
        const providerId = String(subscription.providerSubscriptionId || "");
        return !billing.pendingSubscriptionId || providerId === billing.pendingSubscriptionId;
      });
  const pendingPriceChanged = Boolean(
    pendingCheckoutUrl &&
      pendingRecord &&
      (Math.abs(Number(pendingRecord.amount || 0) - checkoutAmount) >= 0.01 ||
        String(pendingRecord.currency || "ARS").toUpperCase() !==
          String(currency || "ARS").toUpperCase()),
  );
  const pendingPayerEmailChanged = Boolean(
    paymentMode === "recurring" &&
      pendingCheckoutUrl &&
      pendingRecord?.payerEmail &&
      normalizeEmail(pendingRecord.payerEmail) !== normalizeEmail(payerEmail),
  );
  const priceNeedsConfirmation = Boolean(
    billing.pricingOutdated || pendingPriceChanged || pendingPayerEmailChanged,
  );
  const canControlProvider =
    billing.paymentMode === "recurring" &&
    billing.provider === "mercadopago" &&
    Boolean(billing.subscriptionId);
  const canSubmit =
    Boolean(data?.canManage) &&
    Boolean(data?.mercadoPagoConfigured) &&
    selectedCodes.length > 0;
  const featureRequested = searchParams.get("feature");
  const isActive = ["active", "exempt"].includes(billing.status);
  const currentPeriodEnd = billing.currentPeriodEnd
    ? new Date(billing.currentPeriodEnd)
    : null;
  const hasFuturePaidPeriod = Boolean(
    billing.paymentMode === "one_time" &&
      billing.status === "active" &&
      currentPeriodEnd &&
      !Number.isNaN(currentPeriodEnd.getTime()) &&
      currentPeriodEnd > new Date(),
  );
  const oneTimeLocked = Boolean(
    billing.paymentMode === "recurring" &&
      billing.status === "active" &&
      billing.subscriptionId,
  );
  const recurringLocked = hasFuturePaidPeriod;
  const paymentModeChanged = Boolean(
    isActive &&
      ["recurring", "one_time"].includes(billing.paymentMode) &&
      billing.paymentMode !== paymentMode,
  );
  const recurringNeedsAuthorization =
    paymentMode === "recurring" &&
    (!isActive || !billing.isRecurring || paymentModeChanged);
  const payerEmailRequired = Boolean(
    paymentMode === "recurring" &&
      (recurringNeedsAuthorization || paymentNeedsAttention || pendingCheckoutUrl),
  );
  const payerEmailReady = !payerEmailRequired || isValidEmail(payerEmail);
  const hasCheckoutAction =
    paymentMode === "one_time" ||
    recurringNeedsAuthorization ||
    selectionChanged ||
    priceNeedsConfirmation ||
    Boolean(pendingCheckoutUrl);
  const effectivePaymentMode =
    isActive || paymentNeedsAttention
      ? billing.paymentMode === "one_time"
        ? "one_time"
        : "recurring"
      : paymentMode;
  const billingHistory = [
    ...(data?.subscriptions || []).map((item) => ({
      ...item,
      historyType: "recurring",
    })),
    ...(data?.checkouts || []).map((item) => ({
      ...item,
      historyType: "one_time",
    })),
  ].sort(
    (left, right) =>
      new Date(right.createdAt || 0).getTime() -
      new Date(left.createdAt || 0).getTime(),
  );

  React.useEffect(() => {
    if (
      data
      && featureRequested
      && !paymentNeedsAttention
      && !ownerFullSystem
      && !featureWorkspaceOpened.current
    ) {
      featureWorkspaceOpened.current = true;
      setWorkspaceMode("modules");
    }
  }, [data, featureRequested, ownerFullSystem, paymentNeedsAttention]);


  const toggleService = (service, checked) => {
    if (checked) {
      const nextCodes = expandSelection([...selectedCodes, service.code], services);
      const automaticallyAdded = nextCodes.filter(
        (code) => !selectedCodes.includes(code) && code !== service.code,
      );
      setSelectedCodes(nextCodes);
      if (automaticallyAdded.length) {
        const names = automaticallyAdded
          .map((code) => serviceByCode.get(code)?.name)
          .filter(Boolean)
          .join(", ");
        setMessage(`${service.name} necesita ${names}. WebProfit los agregó automáticamente.`);
      }
      return;
    }

    const requiredBy = services.filter(
      (candidate) =>
        selectedCodes.includes(candidate.code) &&
        (candidate.requiredServiceCodes || []).includes(service.code),
    );

    if (requiredBy.length) {
      setMessage(
        `${service.name} no se puede desactivar mientras esté activo ${requiredBy
          .map((candidate) => candidate.name)
          .join(", ")}.`,
      );
      return;
    }

    setSelectedCodes((current) => removeServiceAndDependants(service.code, current, services));
  };

  const changePaymentMode = (nextMode) => {
    if (nextMode === "one_time" && oneTimeLocked) return;
    if (nextMode === "recurring" && recurringLocked) return;
    setPaymentMode(nextMode);
    setCardError("");
    setThreeDSChallenge(null);
  };

  const saveSelection = async () => {
    if (pendingCheckoutUrl && !selectionChanged && !priceNeedsConfirmation) {
      window.location.assign(pendingCheckoutUrl);
      return;
    }
    const response = await run(
      () =>
        paymentMode === "one_time"
          ? planBillingApi.checkout(projectId, {
              serviceCodes: selectedCodes,
              paymentMode: "one_time",
              periodMonths,
            })
          : planBillingApi.subscribe(projectId, {
              serviceCodes: selectedCodes,
              payerEmail: normalizeEmail(payerEmail),
            }),
      paymentMode === "one_time"
        ? "Mercado Pago está listo para cobrar este período."
        : isActive
          ? "Tu plan quedó actualizado."
          : "Tu plan está listo para autorizarse en Mercado Pago.",
    );
    if (response?.checkoutUrl) window.location.assign(response.checkoutUrl);
  };


  const payWithEmbeddedCard = async ({ formData }) => {
    try {
      setWorking(true);
      setError("");
      setCardError("");
      setMessage("");
      const response = await planBillingApi.payWithCard(projectId, {
        paymentMode,
        serviceCodes: selectedCodes,
        periodMonths,
        formData,
      });

      const formEmail = normalizeEmail(formData?.payer?.email || "");
      if (formEmail) setPayerEmail(formEmail);

      if (response?.payment?.threeDS?.required) {
        setThreeDSChallenge({
          ...response.payment.threeDS,
          paymentId: response.payment.paymentId,
        });
        setMessage("El banco necesita una verificación adicional para terminar el pago.");
        return response;
      }

      setThreeDSChallenge(null);
      setMessage(
        response?.message ||
          (paymentMode === "recurring"
            ? "El débito automático quedó autorizado."
            : "El pago fue procesado."),
      );
      await Promise.all([load(), loadProjects()]);
      setWorkspaceMode("overview");
      return response;
    } catch (err) {
      const nextMessage = err?.message || "No se pudo procesar la tarjeta";
      setCardError(nextMessage);
      setError(nextMessage);
      return null;
    } finally {
      setWorking(false);
    }
  };

  const completeThreeDSChallenge = async () => {
    const paymentId = threeDSChallenge?.paymentId;
    if (!paymentId) return;
    try {
      setWorking(true);
      setCardError("");
      setError("");
      await planBillingApi.refresh(projectId, { paymentId });
      setThreeDSChallenge(null);
      setMessage("Verificamos el pago con Mercado Pago.");
      await Promise.all([load(), loadProjects()]);
      setWorkspaceMode("overview");
    } catch (err) {
      const nextMessage = err?.message || "No se pudo verificar el pago";
      setCardError(nextMessage);
      setError(nextMessage);
    } finally {
      setWorking(false);
    }
  };

  const updatePaymentMethod = async () => {
    const response = await run(
      () =>
        planBillingApi.updatePaymentMethod(projectId, {
          payerEmail: normalizeEmail(payerEmail),
        }),
      "Abrimos Mercado Pago para cambiar la tarjeta.",
    );
    if (response?.checkoutUrl) window.location.assign(response.checkoutUrl);
  };

  const pauseSubscription = async () => {
    if (!window.confirm("¿Pausar el plan? La landing seguirá visible, pero las funciones operativas quedarán pausadas hasta que lo reactives.")) return;
    await run(() => planBillingApi.pause(projectId), "Tu plan quedó pausado.");
  };

  const cancelSubscription = async () => {
    if (!window.confirm("¿Cancelar el plan y detener el cobro mensual de WebProfit? La landing seguirá visible, pero las funciones operativas quedarán pausadas.")) return;
    await run(() => planBillingApi.cancel(projectId), "Tu plan quedó cancelado.");
  };

  const actionLabel = pendingCheckoutUrl && !selectionChanged && !priceNeedsConfirmation
    ? "Continuar en Mercado Pago"
    : paymentMode === "one_time"
      ? "Pagar con Mercado Pago"
      : paymentNeedsAttention
        ? "Actualizar tarjeta con Mercado Pago"
        : isActive && billing.isRecurring
          ? selectionChanged || priceNeedsConfirmation
            ? "Actualizar suscripción"
            : "Suscripción activa"
          : "Suscribirme con Mercado Pago";
  const primaryAction =
    paymentNeedsAttention && paymentMode === "recurring" && canControlProvider
      ? updatePaymentMethod
      : saveSelection;
  const modeLocked =
    (paymentMode === "one_time" && oneTimeLocked) ||
    (paymentMode === "recurring" && recurringLocked);
  const primaryDisabled =
    working ||
    !canSubmit ||
    !payerEmailReady ||
    modeLocked ||
    (!paymentNeedsAttention && !hasCheckoutAction);
  const embeddedCardConfigured = Boolean(
    data?.embeddedCardConfigured && data?.mercadoPagoPublicKey,
  );
  const cardDisabled =
    working ||
    !canSubmit ||
    modeLocked ||
    checkoutAmount <= 0 ||
    !embeddedCardConfigured;

  // Keep every primary price on the same source used by the next checkout.
  // When the stored monthly amount differs, it is shown only as historical context.
  const primaryPlanAmount = checkoutAmount;
  const storedAmountDiffers = Boolean(
    paymentMode === "recurring"
      && Number(billing.monthlyAmount || 0) > 0
      && Math.abs(Number(billing.monthlyAmount || 0) - Number(primaryPlanAmount || 0)) >= 0.01,
  );

  const overviewStatus = billingAccess.isGrace
    ? "Pago pendiente"
    : paymentSuspended
      ? "Suspendido"
      : billingAccess.isLandingOnly
        ? "Acceso limitado"
        : trialActive
          ? "Prueba gratis"
          : statusLabel(billing.status);
  const overviewStatusColor = billingAccess.isLandingOnly || paymentSuspended
    ? "error"
    : billingAccess.isGrace
      ? "warning"
      : statusColor(billing.status);
  const overviewDeadlineLabel = trialActive
    ? "Prueba hasta"
    : billingAccess.isGrace
      ? "Regularizar antes de"
      : effectivePaymentMode === "one_time"
        ? "Activo hasta"
        : "Próximo cobro";
  const overviewDeadlineValue = trialActive
    ? dateLabel(billingAccess.trialEndsAt)
    : billingAccess.isGrace
      ? dateLabel(billingAccess.graceUntil)
      : paymentSuspended
        ? "Pendiente"
        : dateLabel(billing.nextPaymentDate || billing.currentPeriodEnd);
  const overviewPaymentLabel = effectivePaymentMode === "one_time"
    ? "pago por período"
    : billing.isRecurring
      ? "débito automático"
      : "modalidad a confirmar";
  const overviewNotice = paymentSuspended
    ? "El proyecto está suspendido por pago. Tus datos siguen guardados y se reactiva al aprobarse el cobro."
    : billingAccess.isGrace
      ? `Tu proyecto sigue funcionando. Regularizá antes del ${dateLabel(billingAccess.graceUntil)} para evitar la pausa de funciones.`
      : billingAccess.isLandingOnly
        ? "La landing sigue visible y las funciones operativas están pausadas hasta activar o regularizar el plan."
        : ownerFullSystem && trialActive
          ? `Precio fijo administrado desde Owner. Tenés acceso completo hasta el ${dateLabel(billingAccess.trialEndsAt)}; al terminar la prueba, si no hay activación vigente, queda sólo la landing.`
        : !data?.mercadoPagoConfigured
          ? "El cobro todavía no está disponible porque falta configurar Mercado Pago en WebProfit."
          : pendingPriceChanged
            ? "El pago pendiente tenía otro importe. El siguiente paso usa el total visible en esta pantalla."
            : billing.pricingOutdated
              ? "Hay un precio actualizado para esta combinación. Se aplicará recién cuando confirmes el próximo cobro."
              : trialActive
                ? `Tenés acceso completo hasta el ${dateLabel(billingAccess.trialEndsAt)} sin cargar una tarjeta.`
                : "";
  const overviewNoticeSeverity = paymentSuspended || billingAccess.isLandingOnly
    ? "error"
    : billingAccess.isGrace || !data?.mercadoPagoConfigured || pendingPriceChanged
      ? "warning"
      : "info";
  const shouldOfferCheckout = Boolean(
    paymentNeedsAttention
      || (!isActive && selectedCodes.length > 0)
      || selectionChanged
      || priceNeedsConfirmation
      || pendingCheckoutUrl,
  );
  const overviewCanCheckout = Boolean(
    data?.canManage
      && data?.mercadoPagoConfigured
      && selectedCodes.length > 0
      && !modeLocked,
  );
  const overviewCheckoutLabel = paymentNeedsAttention
    ? "Regularizar pago"
    : isActive
      ? "Confirmar cambios"
      : "Activar y pagar";


  if (loading && !data) {
    return <DashboardLoadingState label="Preparando tu plan…" minHeight={420} />;
  }

  return (
    <Stack spacing={1.35} sx={{ pb: 3, minWidth: 0 }}>
      {error ? <Alert severity="error" onClose={() => setError("")} sx={{ borderRadius: 2.5, py: 0.25 }}>{error}</Alert> : null}
      {message ? <Alert severity="success" onClose={() => setMessage("")} sx={{ borderRadius: 2.5, py: 0.25 }}>{message}</Alert> : null}

      {workspaceMode === "overview" ? (
        <CompactPlanOverview
          amount={primaryPlanAmount}
          currency={currency}
          status={overviewStatus}
          statusColorName={overviewStatusColor}
          amountLabel={paymentMode === "one_time" ? `· ${periodMonths === 1 ? "1 mes" : `${periodMonths} meses`}` : "/ mes"}
          deadlineLabel={overviewDeadlineLabel}
          deadlineValue={overviewDeadlineValue}
          paymentLabel={overviewPaymentLabel}
          selectedServices={displayedSelectedServices}
          ownerManaged={ownerFullSystem}
          paymentNeedsAttention={paymentNeedsAttention}
          canManage={Boolean(data?.canManage)}
          canCheckout={overviewCanCheckout}
          showCheckout={shouldOfferCheckout}
          checkoutLabel={overviewCheckoutLabel}
          onCheckout={() => setWorkspaceMode("payment")}
          onEdit={() => setWorkspaceMode("modules")}
          storedAmountDiffers={storedAmountDiffers}
          storedAmount={billing.monthlyAmount}
          storedCurrency={billing.currency || currency}
          noticeText={overviewNotice}
          noticeSeverity={overviewNoticeSeverity}
        />
      ) : null}

      {workspaceMode === "modules" ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3.25,
            border: `1px solid ${BRAND.border}`,
            bgcolor: "var(--wp-panel-surface, #fff)",
            overflow: "hidden",
          }}
        >
          {/* MODULE EDITOR · Hidden by default to preserve the anti-scroll overview. */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ sm: "center" }}
            spacing={1}
            sx={{ px: { xs: 1.4, sm: 1.8 }, py: 1.25, borderBottom: `1px solid ${BRAND.border}` }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 950, letterSpacing: ".08em" }}>
                PASO 1 · TU PLAN
              </Typography>
              <Stack direction="row" spacing={0.7} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: { xs: "1.12rem", sm: "1.25rem" } }}>
                  Elegí los módulos que necesitás
                </Typography>
                <Chip size="small" label={`${selectedCodes.length} elegidos · ${money(selectedAmount, currency)}/mes`} sx={{ height: 25, fontWeight: 900 }} />
              </Stack>
            </Box>
            <Button
              onClick={() => {
                setSelectedCodes(expandSelection(currentCodes, services));
                setWorkspaceMode("overview");
              }}
              sx={{ alignSelf: { xs: "flex-start", sm: "center" }, textTransform: "none", fontWeight: 900, color: BRAND.muted }}
            >
              Cancelar
            </Button>
          </Stack>

          {featureRequested ? (
            <Alert severity="info" icon={false} sx={{ borderRadius: 0, py: 0.35, "& .MuiAlert-message": { py: 0.25, fontSize: ".82rem" } }}>
              Querés activar <strong>{featureRequested}</strong>. Seleccionalo y WebProfit agregará automáticamente sus dependencias.
            </Alert>
          ) : null}

          <Box sx={{ p: { xs: 1.2, sm: 1.45 } }}>
            {ownerFullSystem ? (
              <Alert severity="success" sx={{ mb: 1, py: 0.25 }}>
                Precio acordado desde Owner: <strong>{money(selectedAmount, billing.currency || "ARS")}/mes</strong>. Los módulos están incluidos y no se cobran por separado.
              </Alert>
            ) : null}

            {services.length ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2,minmax(0,1fr))",
                    sm: "repeat(3,minmax(0,1fr))",
                    lg: "repeat(4,minmax(0,1fr))",
                  },
                  gap: { xs: 0.7, md: 0.8 },
                  minWidth: 0,
                  "@media (max-width:350px)": { gridTemplateColumns: "minmax(0,1fr)" },
                }}
              >
                {serviceOptions.map((option) => (
                  <ServiceTile
                    key={option.service.code}
                    {...option}
                    disabled={working || paymentNeedsAttention || !data?.canManage || ownerFullSystem}
                    paymentNeedsAttention={paymentNeedsAttention}
                    onToggle={(checked) => toggleService(option.service, checked)}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: BRAND.muted }}>
                No hay módulos comerciales disponibles para configurar.
              </Typography>
            )}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ sm: "center" }}
              spacing={0.9}
              sx={{ mt: 1.2, pt: 1.1, borderTop: `1px solid ${BRAND.border}` }}
            >
              <Typography variant="body2" sx={{ color: BRAND.muted }}>
                Total resultante: <Box component="span" sx={{ color: BRAND.dark, fontWeight: 950 }}>{money(selectedAmount, currency)}/mes</Box>
              </Typography>
              <Stack direction="row" spacing={0.7} sx={{ width: { xs: "100%", sm: "auto" }, "& .MuiButton-root": { textTransform: "none", fontWeight: 950, borderRadius: 2.2 } }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setWorkspaceMode("overview")}
                  sx={{ borderColor: BRAND.border, color: BRAND.dark }}
                >
                  Ver resumen
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={!data?.canManage || selectedCodes.length === 0 || modeLocked || !data?.mercadoPagoConfigured}
                  onClick={() => setWorkspaceMode("payment")}
                  sx={{ bgcolor: BRAND.orange, "&:hover": { bgcolor: BRAND.orangeDark } }}
                >
                  Continuar al pago
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>
      ) : null}

      {workspaceMode === "payment" ? (
        <CheckoutSummary
          selectedServices={displayedSelectedServices}
          selectedAmount={selectedAmount}
          monthlyPricing={monthlyPricing}
          periodPricing={periodPricing}
          periodMonths={periodMonths}
          periodOptions={periodOptions}
          onPeriodMonthsChange={setPeriodMonths}
          currency={currency}
          billing={billing}
          billingAccess={billingAccess}
          paymentNeedsAttention={paymentNeedsAttention}
          paymentMode={paymentMode}
          onPaymentModeChange={changePaymentMode}
          payerEmail={payerEmail}
          onPayerEmailChange={setPayerEmail}
          payerEmailRequired={payerEmailRequired}
          recurringLocked={recurringLocked}
          oneTimeLocked={oneTimeLocked}
          selectionChanged={selectionChanged}
          actionLabel={actionLabel}
          primaryDisabled={primaryDisabled}
          primaryAction={primaryAction}
          working={working}
          mercadoPagoPublicKey={data?.mercadoPagoPublicKey || ""}
          embeddedCardConfigured={embeddedCardConfigured}
          cardDisabled={cardDisabled}
          onCardSubmit={payWithEmbeddedCard}
          cardError={cardError}
          threeDSChallenge={threeDSChallenge}
          onThreeDSComplete={completeThreeDSChallenge}
          recurringActive={Boolean(isActive && billing.isRecurring)}
          onClose={() => setWorkspaceMode("overview")}
        />
      ) : null}

      {workspaceMode === "overview" && canControlProvider && !paymentNeedsAttention ? (
        <Accordion
          elevation={0}
          disableGutters
          sx={{
            borderRadius: "12px !important",
            border: `1px solid ${BRAND.border}`,
            overflow: "hidden",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ minHeight: 46, px: { xs: 1.5, sm: 1.8 }, "& .MuiAccordionSummary-content": { my: .8 } }}>
            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ minWidth: 0 }}>
              <AutorenewRoundedIcon sx={{ fontSize: 19, color: BRAND.orange }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark, lineHeight: 1.15 }}>Administrar renovación</Typography>
                <Typography variant="caption" sx={{ color: BRAND.muted }}>Verificar, cambiar tarjeta, pausar o cancelar.</Typography>
              </Box>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, px: { xs: 1.5, sm: 1.8 }, pb: 1.4 }}>
            {billing.status === "paused" ? (
              <Button
                disabled={working || !data?.canManage}
                startIcon={<PlayCircleRoundedIcon />}
                onClick={() => run(() => planBillingApi.resume(projectId), "Tu plan quedó activo nuevamente.")}
                variant="contained"
                sx={{ bgcolor: BRAND.orange, borderRadius: 2.2, textTransform: "none", fontWeight: 900, "&:hover": { bgcolor: BRAND.orangeDark } }}
              >
                Reactivar plan
              </Button>
            ) : (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={0.6} flexWrap="wrap" useFlexGap sx={{ "& .MuiButton-root": { justifyContent: "flex-start", textTransform: "none", fontWeight: 850 } }}>
                <Button disabled={working} startIcon={<SyncRoundedIcon />} onClick={() => run(() => planBillingApi.refresh(projectId), "Estado actualizado.")}>Verificar estado</Button>
                <Button disabled={working || !data?.canManage || !isValidEmail(payerEmail)} startIcon={<CreditCardRoundedIcon />} onClick={updatePaymentMethod}>Cambiar tarjeta</Button>
                <Button disabled={working || !data?.canManage || billing.status === "cancelled"} startIcon={<PauseCircleRoundedIcon />} onClick={pauseSubscription}>Pausar renovación</Button>
                <Button disabled={working || !data?.canManage || billing.status === "cancelled"} color="error" startIcon={<CancelRoundedIcon />} onClick={cancelSubscription}>Cancelar plan</Button>
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      ) : null}

      {workspaceMode === "overview" ? (
        <Accordion elevation={0} disableGutters sx={{ borderRadius: "14px !important", border: `1px solid ${BRAND.border}`, overflow: "hidden", "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: { xs: 2.25, md: 3 }, py: 0.75 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
              Historial de pagos y cambios
            </Typography>
            <Typography variant="body2" sx={{ display: { xs: "none", sm: "block" }, color: BRAND.muted }}>
              Revisá autorizaciones, importes y estados anteriores.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 1.25, md: 3 }, pb: 3 }}>
          <TableContainer sx={{ maxWidth: "100%" }}>
            <ResponsiveOperationalTable size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Plan elegido</TableCell>
                  <TableCell>Modalidad</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Importe</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billingHistory.map((entry) => (
                  <TableRow key={`${entry.historyType}-${entry.id || entry._id}`} hover>
                    <TableCell>{dateLabel(entry.createdAt)}</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>{subscriptionServicesLabel(entry)}</TableCell>
                    <TableCell>
                      {entry.historyType === "one_time"
                        ? "Pago por período"
                        : entry.provider === "mercadopago"
                          ? "Débito automático"
                          : "Manual"}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={statusLabel(entry.status)} color={statusColor(entry.status)} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900 }}>
                      {money(entry.amount, entry.currency)}
                    </TableCell>
                  </TableRow>
                ))}
                {!billingHistory.length ? (
                  <TableRow>
                    <TableCell colSpan={5}>Todavía no hay pagos o cambios registrados.</TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </ResponsiveOperationalTable>
          </TableContainer>
        </AccordionDetails>
        </Accordion>
      ) : null}
    </Stack>
  );
}
